import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  GetMessage,
  GetMessageWithIntl,
  LoadingIcon,
  Toast,
  error,
  success,
} from '~/shared/components';
import { useErrorHandler } from '~/shared/components/error/ErrorBoundary';
import {
  Accordion,
  Checkbox,
  CommentDialog,
  ConfirmationDialog,
  DetailConfirmationDialog,
  HistoryDialog,
  NavigationIconMenu,
  PageNavigation,
  SurveyReplyDialog,
  EstimateResultAttachmentsDialog,
  EstimateResultAttachmentsResult,
  SystemNotificationSettingDialog,
  Textarea,
  SimpleListView,
} from '~/shared/components/ui';
import { useAuth } from '~/shared/contexts/AuthProvider';
import {
  FullMethodName_ListEstimateResultDetails,
  FullMethodName_ListEstimateResultSummarys,
  FullMethodName_ListEstimateResults,
  FullMethodName_SharedListEstimateSelections,
  HistoryItems,
  PageState,
  ViewId,
  getDateFormatWithTimezone,
  getHistoryItems,
  getSortSettingFromLocalStorage,
  getWorkerExceptionMessage,
  isShowNotificationArea,
  getDateFormat,
  autoDownloadFileOnlyName,
  autoBulkDownload,
  AttachmentItems,
  createAttachmentFiles,
} from '~/shared/utils';
import { mtechnavi, sharelib } from '~/shared/libs/clientsdk';
import { usePagenator } from '~/shared/components/ui/ListView/pagenator';
import { FilterRequest, SystemNotificationSetting } from '~/worker';
import './EstEstimateResultConfirmation.css';
import { CaptionButton, IconButton } from '~/shared/components/ui/Button';
import {
  convertUserReference,
  convertLongToString,
} from '~/shared/utils/converter';
import { formatDisplayContactProperties } from '../EstEstimateRequestConfirmation/parts/formatter';
import { ForumTypeName, useForumTool } from '~/shared/components/tool/Forum';
import { AttachmentFile } from '~/shared/components/ui/Dialog/CommonDialogTypes';

const VIEW_ID: ViewId = 'EST_ESTIMATE_RESULT_CONFIRMATION';
const historyTypeName = 'mtechnavi.api.estimation.EstimateResult';
type EstimateResultSummary = mtechnavi.api.estimation.IEstimateResultSummary;
type EstimateResult = mtechnavi.api.estimation.IEstimateResult;
type EstimateResultDetail = mtechnavi.api.estimation.IEstimateResultDetail;
type SharedEstimateSelection = mtechnavi.api.estimation.IEstimateSelection;

export function EstEstimateResultConfirmation() {
  // ログイン者の情報特定用email
  const myEmail = useAuth().user?.email ?? '';
  const intl = useIntl();
  const navi = useNavigate();
  const handleError = useErrorHandler();
  const [isLoading, setLoading] = useState(false);
  const [isLoadingResult, setLoadingResult] = useState(false);
  const [isLoadingResultDetail, setLoadingResultDetail] = useState(false);
  const [isLoadingSelection, setLoadingSelection] = useState(false);
  const footerRef = useRef<HTMLDivElement>(null);

  // コミュニケーション機能設定
  const { setForumInfo } = useForumTool();

  // 遷移元から値を受け取る
  const location = (useLocation().state as PageState) ?? [];
  // 例外処理用にエラー情報を取得する様修正
  const [isException, setException] = useState<boolean>(false);
  const [pagenateError, setPagenateError] = useState<unknown>({});

  // 開封リクエスト重複防止制御用
  const isEstimateResultOpenRequested = useRef<boolean>(false);

  // PageInfo
  const [isFirstPage, setFirstPage] = useState(false);
  const [isMaxPage, setMaxPage] = useState(false);
  // 履歴通知表示
  const [isNotificationShow, setNotificationShow] = useState<boolean>(false);

  // ダイアログ
  // 明細確認ダイアログ
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  // 履歴ダイアログ
  const [isHistoryOpen, setHistoryOpen] = useState(false);
  // システム通知設定ダイアログ
  const [systemNotificationDialogOpen, setSystemNotificationDialogOpen] =
    useState(false);
  // 回答ダイアログ
  const [sendReplyDialogOpen, setSendReplyDialogOpen] = useState(false);
  // 回答送信確認ダイアログ
  const [sendReplyConfirmationOpen, setSendReplyConfirmationOpen] =
    useState(false);
  // 見積摘要登録ダイアログ
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  // 回答編集ダイアログ
  const [
    estimeteResultAttachmentsDialogOpen,
    setEstimeteResultAttachmentsDialogOpen,
  ] = useState(false);
  // 回答ファイル
  const [resultAttachmentItems, seResultAttachmentItems] = useState<
    AttachmentItems[]
  >([]);

  // Item情報
  const [item, setItem] = useState<EstimateResultSummary>();
  const estimateResultData = useRef<EstimateResult>({});
  const estimateResultDetailData = useRef<EstimateResultDetail[]>([]);
  const estimateSelectionData = useRef<SharedEstimateSelection>({});
  const [historys, setHistorys] = useState<HistoryItems[]>([]);
  const [isSendReply, setSendReply] = useState(false);
  const [replyComment, setReplyCommnet] = useState('');
  const [declineCheckbox, setDeclineCheckbox] = useState<string[] | undefined>(
    []
  );
  const [estimateRemarksComment, setEstimateRemarksComment] = useState('');
  // アクションID一時管理
  const detailConfirmationDialogId = useRef<string>();

  // メッセージ
  const confirmationMessage = {
    id: 'C0000011',
    value: {
      $1: GetMessageWithIntl(intl, { id: 'send' }),
    },
  };
  const historyMessage = GetMessageWithIntl(intl, {
    id: 'I0000005',
    value: { $1: historys?.length > 0 ? historys[0].slip_category : '' },
  });
  const toastSuccess = GetMessageWithIntl(intl, { id: 'I0000001' });

  // 添付ファイル（依頼）
  interface AttachmentRequestViewType {
    assetId: string;
    filename: string;
    remarks: string;
  }

  // 添付ファイル（回答）
  interface AttachmentResultViewType {
    assetId: string;
    filename: string;
  }

  // ローディング制御
  useEffect(() => {
    if (isLoadingResult || isLoadingResultDetail || isLoadingSelection) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [isLoadingResult, isLoadingResultDetail, isLoadingSelection]);

  // エラー
  useEffect(() => {
    if (isException) {
      error(getWorkerExceptionMessage(intl, pagenateError));
    }
  }, [intl, isException, pagenateError]);

  // ナビゲーションメニュー
  const navigationIconItems = (): NavigationIconMenu[] => {
    const iconItems: NavigationIconMenu[] = [];
    iconItems.push({
      name: 'history',
      displayName: '履歴',
      func: () => setHistoryOpen(true),
    });
    iconItems.push({
      name: 'recipients',
      displayName: '通知設定',
      func: () => {
        setSystemNotificationDialogOpen(true);
      },
    });
    return iconItems;
  };

  // paginator の ID 取得処理
  const getTargetId = () => {
    const urlPathName = window.location.pathname;
    const splitPath = urlPathName.split('/');
    if (location.ids) {
      return location.ids ?? [];
    } else {
      if (splitPath.length > 1) {
        const idPath = splitPath[splitPath.length - 1];
        const endpointPath = splitPath[splitPath.length - 2];
        if (
          idPath !== 'est-estimate-result-confirmation' &&
          endpointPath === 'est-estimate-result-confirmation'
        ) {
          return [idPath];
        }
      }
    }
    return [];
  };

  // pagenatorに基本情報をセット
  const [page, dispatch] = usePagenator({
    fullMethodName: FullMethodName_ListEstimateResultSummarys,
    pageNumber: location.confirmationViewOption?.pageNumber ?? 1,
    pageSize: 1,
    maxPageNumber: 1,
    requestBody:
      location.sourceViewId !== 'TASK_LIST'
        ? { estimateResultIds: getTargetId() }
        : { estimateRequestIds: location.ids },
    filter:
      location.sourceViewId !== 'TASK_LIST'
        ? {
            $or: [{ estimateResultSummaryId: { $in: getTargetId() } }],
          }
        : {},
    sort: getSortSettingFromLocalStorage('EST_ESTIMATE_RESULT_LIST', myEmail),
    items: [],
    originalItems: [],
    onError(err) {
      setException(true);
      setPagenateError(err);
      handleError(err);
    },
  });

  // pageデータ変更時の処理
  useEffect(() => {
    const items = page.originalItems as unknown as EstimateResultSummary[];
    setItem(items.pop());
    setFirstPage(page.pageNumber === 1);
    setMaxPage(page.pageNumber === page.maxPageNumber);
    setNotificationShow(false);
  }, [page, setForumInfo]);

  const handleReload = () => {
    dispatch({
      type: 'reload',
      fullMethodName: FullMethodName_ListEstimateResultSummarys,
      onChangeLoadingState: (v) => {
        setLoading(v);
      },
    });
  };

  // ページ戻し・送り処理
  const handleMovePage = (pageNumber: number) => {
    const n = Math.min(Math.max(1, pageNumber), page.maxPageNumber);
    dispatch({
      type: 'query',
      fullMethodName: FullMethodName_ListEstimateResultSummarys,
      pageNumber: n,
    });
  };

  // 初回画面用データ
  useEffect(() => {
    handleReload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!item) {
      return;
    }
    (async () => {
      setLoading(true);
      // 未開封の場合は開封リクエストをしてリロードする
      if (
        item?.status?.systemName == 'B00' &&
        !isEstimateResultOpenRequested.current
      ) {
        try {
          isEstimateResultOpenRequested.current = true;
          await window.App.services.ui.worker.apiCall({
            actionName: 'openEstimateRequestUnit',
            request: {
              estimateRequestUnitId: item?.estimateResultSummaryId,
            },
          });
          handleReload();
          return;
        } catch (err) {
          error(getWorkerExceptionMessage(intl, err));
          setLoading(false);
          return;
        } finally {
          isEstimateResultOpenRequested.current = false;
        }
      }

      try {
        await Promise.all([
          getEstiamteResult(), // 回答データ
          getEstimateResultDetail(), // 回答詳細データ
          getEstimateSelection(), // 回答選考データ
          getHistory(), // 履歴データ,
        ]);

        // フォーラム設定
        setForumInfo({
          typeName: ForumTypeName.Estimate,
          resourceId: estimateResultData.current?.estimateRequestId ?? '',
          companyIds: [
            estimateResultData.current?.estimateRequest?.requestingCompany
              ?.companyId ?? '',
          ],
        });
      } catch (err) {
        error(getWorkerExceptionMessage(intl, err));
      } finally {
        setLoading(false);
      }
    })();

    // データ取得時だけにしたいので例外的に除去
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, intl]);

  // 履歴一覧を取得
  const getHistory = async () => {
    try {
      const datas = await getHistoryItems(intl, historyTypeName, [
        item?.estimateResultSummaryId ?? '',
      ]);

      setHistorys(datas);
      // 最新の履歴通知レベルによる制御
      setNotificationShow(isShowNotificationArea(datas));
    } catch (err) {
      error(getWorkerExceptionMessage(intl, err));
    }
  };

  // 見積回答情報
  const getEstiamteResult = async () => {
    setLoadingResult(true);
    try {
      const listOption: FilterRequest = {
        action: 'reload',
        fullMethodName: FullMethodName_ListEstimateResults,
        filter: {},
        requestBody: {
          estimateResultIds: [item?.estimateResultSummaryId ?? ''],
        },
        sort: [],
      };
      const resList = (await window.App.services.ui.worker.filter(
        listOption
      )) as mtechnavi.api.estimation.IListEstimateResultsResponse;
      const listItem: EstimateResult[] = resList.items ?? [];
      estimateResultData.current = listItem.length > 0 ? listItem[0] : {};
      setEstimateRemarksComment(
        estimateResultData.current?.estimateRemarks ?? ''
      );
      putResultAttachmentItem(
        estimateResultData.current?.estimateResultAttachments ?? []
      );
    } catch (err) {
      error(getWorkerExceptionMessage(intl, err));
    } finally {
      setLoadingResult(false);
    }
  };

  /**
   * 回答ファイルのセット
   */
  const putResultAttachmentItem = (attachmentItems: sharelib.IAttachment[]) => {
    const resultItems: AttachmentItems[] = [];
    attachmentItems.map((item) => {
      resultItems.push({
        id: item.assetId ?? '',
        category: item.category ?? {},
        assetId: item.assetId ?? '',
        filename: item.filename ?? '',
        mimeType: item.mimeType ?? '',
        remarks: item.remarks ?? '',
        linkType: item.linkType ?? {},
      });
    });
    seResultAttachmentItems([...resultItems]);
  };

  // 見積回答詳細
  const getEstimateResultDetail = async () => {
    setLoadingResultDetail(true);
    try {
      const listOption: FilterRequest = {
        action: 'reload',
        fullMethodName: FullMethodName_ListEstimateResultDetails,
        filter: {},
        requestBody: {
          estimateResultIds: [item?.estimateResultSummaryId ?? ''],
        },
        sort: [
          {
            'estimateRequestDetail.detailAutoName': 'asc',
          },
        ],
      };
      const resList = (await window.App.services.ui.worker.filter(
        listOption
      )) as mtechnavi.api.estimation.IListEstimateResultDetailsResponse;
      const listItem: EstimateResultDetail[] = resList.items ?? [];
      estimateResultDetailData.current = listItem;
    } catch (err) {
      error(getWorkerExceptionMessage(intl, err));
    } finally {
      setLoadingResultDetail(false);
    }
  };

  // 見積選考
  const getEstimateSelection = async () => {
    setLoadingSelection(true);
    try {
      const listOption: FilterRequest = {
        action: 'reload',
        fullMethodName: FullMethodName_SharedListEstimateSelections,
        filter: {},
        requestBody: {
          estimateResultIds: [item?.estimateResultSummaryId ?? ''],
        },
        sort: [],
      };
      const resList = (await window.App.services.ui.worker.filter(
        listOption
      )) as mtechnavi.api.estimation.ISharedListEstimateSelectionsResponse;
      const listItem: SharedEstimateSelection[] = resList.items ?? [];
      estimateSelectionData.current = listItem.length > 0 ? listItem[0] : {};
    } catch (err) {
      error(getWorkerExceptionMessage(intl, err));
    } finally {
      setLoadingSelection(false);
    }
  };

  const renderFooter = () => {
    if (['B00', 'B01'].includes(item?.status?.systemName ?? '')) {
      return (
        <div className="footer" ref={footerRef}>
          <div className="footer-contents">
            <div className="input-line no-space">
              <div className="item-group-100 no-space">
                <CaptionButton
                  name="button"
                  buttonType="basic"
                  caption={GetMessageWithIntl(intl, {
                    id: 'estimateResult.sendReply',
                    prefixId: VIEW_ID,
                  })}
                  onClick={() => {
                    // 回答ファイルチェック
                    console.log(
                      '回答ファイルチェック',
                      declineCheckbox,
                      resultAttachmentItems
                    );
                    // 辞退がOFFの場合
                    if ((declineCheckbox ?? []).length === 0) {
                      // 添付ファイル（回答）が添付されていないかチェック
                      if (resultAttachmentItems.length === 0) {
                        error([GetMessageWithIntl(intl, { id: 'E0000133' })]);
                        return;
                      }
                    }
                    // 辞退がONの場合、チェック不要
                    setSendReplyDialogOpen(true);
                  }}
                />
                <div className="decline-checkbox">
                  <Checkbox
                    name={'decline'}
                    items={[
                      {
                        value: '1',
                        displayName: GetMessageWithIntl(intl, {
                          id: 'estimateResult.decline',
                          prefixId: VIEW_ID,
                        }),
                      },
                    ]}
                    columns={['decline']}
                    value={declineCheckbox}
                    onChangeState={setDeclineCheckbox}
                    validateOption={{ isSkippedValidation: true }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    } else {
      return <></>;
    }
  };

  const handleSaveRemarks = async () => {
    setLoading(true);

    try {
      const req: mtechnavi.api.estimation.IEstimateResult = {
        estimateResultId: estimateResultData?.current?.estimateResultId,
        estimateRequestId: estimateResultData?.current?.estimateRequestId,
        estimateRequest: estimateResultData?.current?.estimateRequest,
        estimateRequestUnit: estimateResultData?.current?.estimateRequestUnit,
        systemNotificationUsers:
          estimateResultData?.current?.systemNotificationUsers,
        status: null,
        deliveryTimeDt: null,
        deliveryTimeRemarks: null,
        leadTimeNum: null,
        leadTimeUnit: null,
        estimateExpireDt: null,
        estimateRemarks: estimateRemarksComment,
        remarks: null,
        estimateResultAttachments:
          estimateResultData?.current?.estimateResultAttachments,
        createdAt: estimateResultData?.current?.createdAt,
        updatedAt: estimateResultData?.current?.updatedAt,
      };
      await window.App.services.ui.worker.apiCall({
        actionName: 'updateEstimateResult',
        request: req,
      });
      success([toastSuccess]);
      handleReload && handleReload();
    } catch (err) {
      error(getWorkerExceptionMessage(intl, err));
      throw err;
    } finally {
      setLoading(false);
      setCommentDialogOpen(false);
    }
  };

  const handleResultAttachments = async (
    result: EstimateResultAttachmentsResult
  ) => {
    setLoading(true);
    try {
      const tmpData: AttachmentFile[] = (await createAttachmentFiles(
        result.files,
        'B11'
      )) as unknown as AttachmentFile[];

      const req: mtechnavi.api.estimation.IEstimateResult = {
        estimateResultId: estimateResultData?.current?.estimateResultId,
        estimateRequestId: estimateResultData?.current?.estimateRequestId,
        estimateRequest: estimateResultData?.current?.estimateRequest,
        estimateRequestUnit: estimateResultData?.current?.estimateRequestUnit,
        systemNotificationUsers:
          estimateResultData?.current?.systemNotificationUsers,
        status: null,
        deliveryTimeDt: null,
        deliveryTimeRemarks: null,
        leadTimeNum: null,
        leadTimeUnit: null,
        estimateExpireDt: null,
        estimateRemarks: estimateResultData?.current?.estimateRemarks,
        remarks: null,
        estimateResultAttachments: tmpData,
        createdAt: estimateResultData?.current?.createdAt,
        updatedAt: estimateResultData?.current?.updatedAt,
      };
      await window.App.services.ui.worker.apiCall({
        actionName: 'updateEstimateResult',
        request: req,
      });
      seResultAttachmentItems([...tmpData]);

      success([toastSuccess]);
      handleReload && handleReload();
    } catch (err) {
      error(getWorkerExceptionMessage(intl, err));
      throw err;
    } finally {
      setLoading(false);
      setEstimeteResultAttachmentsDialogOpen(false);
    }
  };

  /**
   * システム通知設定ダイアログの確定処理
   */
  const handleSystemNotificationSetting = async (result: string) => {
    setLoading(true);
    try {
      const userReference = await convertUserReference(myEmail);
      const req: SystemNotificationSetting = {
        recordId: item?.estimateResultSummaryId ?? '',
        appendUser:
          result === 'setAsNotificationDestination' ? userReference : null,
        removeUser:
          result !== 'setAsNotificationDestination' ? userReference : null,
      };
      await window.App.services.ui.worker.apiCall({
        actionName: 'updateEstimateResultSystemNotificationSetting',
        request: req,
      });
      success([toastSuccess]);
      handleReload();
    } catch (err) {
      error(getWorkerExceptionMessage(intl, err));
    }
  };

  const handleSnedReply = async () => {
    setLoading(true);
    const req: mtechnavi.api.estimation.ISharetoEstimateResultWithRelatedItemsRequest =
      {
        estimateResultId: item?.estimateResultSummaryId,
        comment: replyComment,
        declined: !isSendReply,
      };
    try {
      await window.App.services.ui.worker.apiCall({
        actionName: 'sharetoEstimateResultWithRelatedItems',
        request: req,
      });
      success([toastSuccess]);
    } catch (err) {
      error(getWorkerExceptionMessage(intl, err));
      throw err;
    } finally {
      setLoading(false);
    }
    setSendReplyConfirmationOpen(false);
    setSendReplyDialogOpen(false);
    navi('/estimate/est-estimate-result-list');
  };

  const formatContactuser = () => {
    const contactInfoList: string[] = [];
    estimateResultData?.current?.estimateRequestUnit?.contactUsers?.map(
      (user) => {
        contactInfoList.push(`${user?.displayName}(${user?.email})`);
      }
    );
    return contactInfoList.join(',');
  };

  const formatAttachmentRequestViewListData = () => {
    const viewList: AttachmentRequestViewType[] = [];
    estimateResultData?.current?.estimateRequest?.attachments?.map((v) => {
      viewList.push({
        assetId: v.assetId ?? '',
        filename: v.filename ?? '',
        remarks: v.remarks ?? '',
      });
    });
    return viewList;
  };

  const formatAttachmentResultViewListData = () => {
    const viewList: AttachmentResultViewType[] = [];
    estimateResultData?.current?.estimateResultAttachments?.map((v) => {
      viewList.push({
        assetId: v.assetId ?? '',
        filename: v.filename ?? '',
      });
    });
    return viewList;
  };

  return (
    <>
      <Container viewId={VIEW_ID}>
        <div className="EstEstimateResultConfirmation">
          <div className="header">
            <PageNavigation
              backpagePath="/estimate/est-estimate-result-list"
              pageInfo={{
                isVisibleMoveNavi: true,
                isFirstPage,
                isMaxPage,
                pageNumber: page.pageNumber,
              }}
              iconItems={navigationIconItems()}
              handleMovePage={handleMovePage}
              infoOption={{
                lastUpdateInfo: {
                  isVisibleUpdateInfo: false,
                },
                issuerInfo: { isVisibleIssuerInfo: false },
              }}
            />
          </div>
          <div className="scroll-main-contents-area">
            <div className="info">
              <div className="basic-info-body">
                {/* ////通知枠//// */}
                <div
                  className={`notification-area ${
                    isNotificationShow ? '' : 'close'
                  }`}
                >
                  {/* {履歴通知エリア} */}
                  <div className="input-line">
                    <div className="item-group-100">
                      <div className="w-50">
                        <div className="notification-display-area">
                          {historyMessage}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <Accordion
                  title={GetMessageWithIntl(intl, {
                    viewId: VIEW_ID,
                    id: 'request',
                  })}
                >
                  <div className="input-line label-margin">
                    <div className="item-group-100">
                      <div className="w-77">
                        {/* 件名 */}
                        <h3 className="strong">{item?.displayName ?? ''}</h3>
                      </div>
                      <div className="w-23 ta-right underline">
                        {/* 依頼元情報 */}
                        {GetMessage({
                          id: 'surveyRequesterInfomation',
                          prefixId: VIEW_ID,
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="input-line label-margin">
                    <div className="item-group-100">
                      <div className="w-66">
                        {/* ステータス */}
                        {`${item?.status?.displayNameLang?.ja ?? ''}
                          ${
                            estimateSelectionData?.current.feedbackCategory
                              ?.displayNameLang?.ja
                              ? ':' +
                                estimateSelectionData?.current.feedbackCategory
                                  ?.displayNameLang?.ja
                              : ''
                          }`}
                      </div>
                      <div className="w-33 ta-right">
                        {/* 依頼(通知)番号*/}
                        <span className="label">
                          {GetMessageWithIntl(intl, {
                            id: 'estimateRequestUnitAutoName',
                            prefixId: VIEW_ID,
                          })}
                        </span>
                        <span>
                          {convertLongToString(
                            item?.estimateRequestUnitAutoName
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="input-line label-margin">
                    <div className="item-group-100">
                      <div className="w-66">
                        {/* 回答期日 */}
                        <span className="label">
                          {GetMessageWithIntl(intl, {
                            id: 'estimateDeadlineDt',
                            prefixId: VIEW_ID,
                          })}
                        </span>
                        {`${
                          getDateFormatWithTimezone(
                            estimateResultData?.current?.estimateRequest
                              ?.estimateDeadlineDt
                          ) ?? ''
                        } ${
                          estimateResultData?.current?.estimateRequest
                            ?.desiredReceiveType?.displayNameLang?.ja ?? ''
                        }${
                          estimateResultData?.current?.estimateRequest
                            ?.desiredReceiveType?.displayNameLang?.ja &&
                          estimateResultData?.current?.estimateRequest
                            ?.desiredReceiveRemarks
                            ? ' / '
                            : ''
                        }${
                          estimateResultData?.current?.estimateRequest
                            ?.desiredReceiveRemarks ?? ''
                        }`}
                      </div>
                      <div className="w-33 ta-right">
                        <span className="label">
                          {GetMessageWithIntl(intl, {
                            id: 'estimateRequest.sendedAt',
                            prefixId: VIEW_ID,
                          })}
                        </span>
                        {getDateFormat(
                          estimateResultData?.current?.estimateRequest
                            ?.sendedAt ?? '',
                          'YYYY/MM/DD'
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="input-line label-margin">
                    <div className="item-group-100">
                      <div className="w-100 ta-right">
                        {/* 依頼元*/}
                        <span>
                          {estimateResultData?.current?.estimateRequest
                            ?.requestingCompany?.displayNameLang?.ja ?? ''}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="input-line label-margin">
                    <div className="item-group-100">
                      <div className="w-100 ta-right">
                        {/* 問合せ先*/}
                        <span className="label">
                          {GetMessageWithIntl(intl, {
                            id: 'contactProperties',
                            prefixId: VIEW_ID,
                          })}
                        </span>
                        <span>
                          {formatDisplayContactProperties(
                            estimateResultData?.current?.estimateRequest
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="input-line label-margin">
                    <div className="item-group-100">
                      <div className="w-100">
                        {/* TO */}
                        <span className="label">
                          {GetMessageWithIntl(intl, {
                            id: 'estimateRequest.to',
                            prefixId: VIEW_ID,
                          })}
                        </span>
                        {formatContactuser()}
                      </div>
                    </div>
                  </div>

                  <div className="input-line">
                    <div className="item-group-100">
                      <div className="w-66">
                        {/* お知らせ事項 */}
                        <Textarea
                          name="notice"
                          value={
                            estimateResultData?.current?.estimateRequest
                              ?.notice ?? ''
                          }
                          columns={['notice']}
                          className="w-100 mh-middle"
                          disabled={true}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="input-line mb-2">
                    <div className="item-group-100">
                      <div className="w-66">
                        <SimpleListView
                          data={formatAttachmentRequestViewListData()}
                          actionOptions={{
                            onRowClick: (item: AttachmentItems) => {
                              autoDownloadFileOnlyName(
                                item.filename ?? '',
                                item.assetId ?? ''
                              );
                            },
                            onFullDownLoad: () => {
                              autoBulkDownload(
                                (
                                  formatAttachmentRequestViewListData() ?? []
                                ).map((item) => item.assetId ?? ''),
                                intl,
                                VIEW_ID
                              );
                            },
                          }}
                          viewOptions={{
                            columns: [
                              {
                                propertyName: 'filename',
                                header: {
                                  id: 'attachmentSimpleViewList.requestFilename',
                                  prefixId: VIEW_ID,
                                },
                              },
                              {
                                propertyName: 'remarks',
                                header: {
                                  id: 'attachmentSimpleViewList.remarks',
                                  prefixId: VIEW_ID,
                                },
                                readonly: true,
                              },
                            ],
                            previewRowCount: 5,
                            keyColumn: 'assetId',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </Accordion>

                <Accordion
                  title={GetMessageWithIntl(intl, {
                    viewId: VIEW_ID,
                    id: 'result',
                  })}
                >
                  <div className="input-line">
                    <div className="item-group-100">
                      <div className="w-33">
                        {/* 添付ファイル（回答） */}
                        <SimpleListView
                          data={formatAttachmentResultViewListData()}
                          actionOptions={{
                            onRowClick: (item: AttachmentItems) => {
                              autoDownloadFileOnlyName(
                                item.filename ?? '',
                                item.assetId ?? ''
                              );
                            },
                            onFullDownLoad: () => {
                              autoBulkDownload(
                                (
                                  formatAttachmentResultViewListData() ?? []
                                ).map((item) => item.assetId ?? ''),
                                intl,
                                VIEW_ID
                              );
                            },
                          }}
                          viewOptions={{
                            columns: [
                              {
                                propertyName: 'filename',
                                header: {
                                  id: 'attachmentSimpleViewList.resultFilename',
                                  prefixId: VIEW_ID,
                                },
                              },
                            ],
                            previewRowCount: 5,
                            keyColumn: 'assetId',
                          }}
                        />
                      </div>
                      {/* ファイル編集(見積回答ステータスが未開封、未回答のみ編集アイコンを表示) */}
                      {['B00', 'B01'].includes(
                        item?.status?.systemName ?? ''
                      ) && (
                        <div className="w-33">
                          <CaptionButton
                            name=""
                            caption="ファイル編集"
                            onClick={() => {
                              setEstimeteResultAttachmentsDialogOpen(true);
                            }}
                            buttonType="basic"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="input-line mb-6">
                    <div className="item-group-100">
                      <div className="w-66">
                        {/* 見積摘要(見積回答ステータスが未開封、未回答のみ編集アイコンを表示) */}
                        {['B00', 'B01'].includes(
                          item?.status?.systemName ?? ''
                        ) && (
                          <IconButton
                            name="editRemarks"
                            iconType="edit"
                            caption="編集"
                            className="memoEdit"
                            buttonType="basic"
                            onClick={() => {
                              setCommentDialogOpen(true);
                            }}
                          />
                        )}
                        <Textarea
                          name="estimateResult.estimateRemakrs"
                          className="w-100 mh-middle"
                          value={
                            estimateResultData.current.estimateRemarks ?? ''
                          }
                          columns={['estimateResult.estimateRemakrs']}
                          labelId={`${VIEW_ID}.estimateResult.estimateRemarks`}
                          disabled={true}
                        />
                      </div>
                    </div>
                  </div>
                </Accordion>
              </div>
            </div>
          </div>
          {/* footerエリア */}
          {renderFooter()}
        </div>
        <Toast />
      </Container>
      {/* システム通知設定ダイアログ */}
      <SystemNotificationSettingDialog
        isOpen={systemNotificationDialogOpen}
        inputData={estimateResultData.current.systemNotificationUsers ?? []}
        onDecision={(result) => {
          handleSystemNotificationSetting(result);
        }}
        onCancel={() => {
          setSystemNotificationDialogOpen(false);
        }}
      />
      {/* 履歴ダイアログ */}
      <HistoryDialog
        isOpen={isHistoryOpen}
        messageOption={{
          headerLabelId: {
            id: 'history',
            prefixId: 'DIALOG_TITLE',
          },
        }}
        inputOption={{
          data: historys ?? [],
          keyColumn: 'history_id',
          columns: [
            {
              propertyName: 'slip_category',
              width: '12rem',
              header: {
                prefixId: 'HistoryDialog',
                id: 'slip_category',
              },
            },
            {
              propertyName: 'occurred_at',
              width: '12rem',
              header: {
                prefixId: 'HistoryDialog',
                id: 'occurred_at',
              },
            },
            {
              propertyName: 'content',
              header: {
                prefixId: 'HistoryDialog',
                id: 'content',
              },
            },
          ],
        }}
        onCancel={() => {
          setHistoryOpen(false);
        }}
      />
      {/* 明細確認ダイアログ */}
      <DetailConfirmationDialog
        isOpen={detailDialogOpen}
        inputOption={{
          detailInfo:
            estimateResultDetailData.current.find(
              (v) =>
                v.estimateResultDetailId === detailConfirmationDialogId.current
            )?.estimateRequestDetail ?? {},
        }}
        onCancel={() => {
          setDetailDialogOpen(false);
        }}
      />
      {/* 回答送信ダイアログ */}
      <SurveyReplyDialog
        isOpen={sendReplyDialogOpen}
        outputOption={{
          mode: 'estimateResult',
          isDisabled: true,
          isCanPartialReply: false,
          initialItem:
            (declineCheckbox?.length ?? 0) > 0 && declineCheckbox![0] === '1'
              ? 'decline'
              : 'sendReply',
        }}
        messageOption={{
          headerLabelId: {
            id: 'survey_reply',
            prefixId: 'DIALOG_TITLE',
          },
        }}
        onCancel={() => {
          setSendReplyDialogOpen(false);
        }}
        onDecision={(result) => {
          result.replyStatus === 'sendReply'
            ? setSendReply(true)
            : setSendReply(false);
          setReplyCommnet(result.comment);
          setSendReplyConfirmationOpen(true);
        }}
      />
      {/* 回答送信確認ダイアログ */}
      <ConfirmationDialog
        isOpen={sendReplyConfirmationOpen}
        viewMessage={confirmationMessage}
        messageLabelId={{ prefixId: 'DIALOG_MESSAGE', viewId: VIEW_ID }}
        onDecision={() => {
          handleSnedReply();
        }}
        onCancel={() => setSendReplyConfirmationOpen(false)}
      />
      {/* 見積摘要登録ダイアログ */}
      <CommentDialog
        isOpen={commentDialogOpen}
        inputOption={{
          comment: estimateRemarksComment,
          modeType: 'save',
        }}
        inputStateOption={{ onChangeComment: setEstimateRemarksComment }}
        messageOption={{
          headerLabelId: {
            id: 'estimateRemarks',
            prefixId: 'DIALOG_TITLE',
          },
          captionLabelId: {
            id: 'estimateRemarks',
            prefixId: 'DIALOG_CAPTION',
          },
          decisionLabelId: {
            id: 'save',
          },
        }}
        onDecision={() => handleSaveRemarks()}
        onCancel={() => setCommentDialogOpen(false)}
      />
      {/* 回答ファイル編集 */}
      <EstimateResultAttachmentsDialog
        isOpen={estimeteResultAttachmentsDialogOpen}
        inputOption={{
          files: resultAttachmentItems,
        }}
        messageOption={{
          headerLabelId: {
            id: 'estimate_result_attachments',
            prefixId: 'DIALOG_TITLE',
          },
        }}
        uploadOption={{
          validateOption: {
            maxFileSizeInMebis: 50,
            maxFileCount: 10,
          },
        }}
        onCancel={() => {
          setEstimeteResultAttachmentsDialogOpen(false);
        }}
        onDecision={(result) => handleResultAttachments(result)}
        onChangeLoadingState={(isLoading) => {
          setLoading(isLoading);
        }}
      />

      {isLoading && <LoadingIcon />}
    </>
  );
}
